import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  BackgroundJob,
  TaskScopeNameEnum,
  TaskTimelineCompletionGroupByEnum,
  useDashboardTimelineTaskIdsLazyQuery,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { Values } from '@lib/interfaces/form';
import { EventBusEvents, jobEventBus } from '@lib/utils/eventBus';
import { FieldNames, FormTypeValue } from 'components/GenerateReportForm/types';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { getDashboardTimelineTasksFilters } from 'utils/fetch/hooks/useDashboardTimelineTasksQueryHook';
import getPreparedReportDataFromValues from './getPreparedReportDataFromValues';

type Args = {
  basePeriodEndParam: string;
  basePeriodStartParam: string;
  completionGroupBy: TaskTimelineCompletionGroupByEnum;
  filterParams: Values;
  onCompleted: () => void;
  taskTimelineReportCreateMutation: (v: object) => Promise<unknown>;
  scopeName: string;
  searchQuery?: string | null;
};

export default function useGenerateReportActions(args: Args) {
  const {
    completionGroupBy,
    filterParams,
    scopeName,
    searchQuery,
    onCompleted,
    taskTimelineReportCreateMutation,
    basePeriodStartParam,
    basePeriodEndParam,
  } = args;
  const { t } = useTranslation();
  const [fetchReportTaskIds] = useDashboardTimelineTaskIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  // Covers the ids fetch that runs before the mutation, whose own `loading`
  // only starts once it is sent.
  const [isPreparing, setIsPreparing] = useState(false);

  // The report has its own Period, so its tasks are the grid's filters
  // re-applied over that period — not the ids of the rows on screen, whose
  // window may differ (and may exceed the per-site task limit for nothing).
  const getReportTaskIds = async (period: string[]) => {
    const { data, error } = await fetchReportTaskIds({
      variables: {
        scope: { name: scopeName as TaskScopeNameEnum },
        filters: getDashboardTimelineTasksFilters({
          filterParams: {
            ...filterParams,
            [TasksFilterFieldNames.Period]: period,
          },
          searchQuery,
        }),
      },
    });
    if (error) throw error;
    return (data?.data.collection || []).map(({ id }) => id);
  };

  const onReportCreateAction = (values: Values) => {
    const formType = _get(values, [FieldNames.FormType, 'value']);
    const period = _get(values, FieldNames.Period, [
      basePeriodStartParam,
      basePeriodEndParam,
    ]) as string[];
    setIsPreparing(true);
    getReportTaskIds(period)
      .then((taskIds) => {
        const mutation = taskTimelineReportCreateMutation({
          variables: {
            input: getPreparedReportDataFromValues({
              completionGroupBy,
              values,
              taskIds,
              filterParams,
              basePeriodStartParam,
              basePeriodEndParam,
            }),
          },
          onCompleted,
        });
        // Handed over to the mutation's own `loading` in the same render.
        setIsPreparing(false);
        return mutation;
      })
      .then((response) => {
        const backgroundJob = _get(response, 'data.data') as
          BackgroundJob | undefined;
        if (backgroundJob) {
          jobEventBus.emit(EventBusEvents.JobCreated, backgroundJob);
        }
        if (formType === FormTypeValue.Download) {
          toast({ content: t('get-tasks-report-download-success') });
        } else if (formType === FormTypeValue.Email) {
          toast({ content: t('get-tasks-report-email-success') });
        }
      })
      .catch((error) => {
        setIsPreparing(false);
        const message = _get(error, 'message');
        let scopeMessage;
        getGraphQLErrors(error)?.forEach((errorItem) => {
          const details = errorItem?.extensions?.details;
          const errorMessageByScope = Array.isArray(details)
            ? details.find(({ path }) => path[0] === 'scope')?.message
            : undefined;
          if (errorMessageByScope) scopeMessage = errorMessageByScope;
        });
        const content = scopeMessage || message || t('something-went-wrong');
        toast({ content });
      });
  };

  return {
    onReportCreateAction,
    isReportPreparing: isPreparing,
  };
}
